// Blueprint: forge/blueprint/frontend/architecture/001-shell-composition.md
import { observer } from 'mobx-react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDependencies } from '@servicetitan/react-ioc';
import { SideNav } from '@servicetitan/anvil2';

import { AppStore, NavItem } from '../stores/app.store';
import { FC } from 'react';

interface SidebarItemProps {
    item: NavItem;
}

const SidebarItem: FC<SidebarItemProps> = ({ item }) => {
    const history = useHistory();
    const { pathname } = useLocation();

    return (
        <SideNav.Link
            id={item.path}
            href={history.createHref({ pathname: item.path })}
            active={pathname === item.path || pathname.startsWith(`${item.path}/`)}
            onClick={e => {
                e.preventDefault();
                history.push(item.path);
            }}
        >
            {item.label}
        </SideNav.Link>
    );
};

export const Sidebar: FC = observer(() => {
    const [{ sections }] = useDependencies(AppStore);

    /*
     * SideNav.Link's built-in `pathname` routing calls the native `history.pushState`, which bypasses the
     * HashBrowserRouter (wrong URL form + no route re-render). Instead drive the router's history on click,
     * and let `href` carry the hash URL (`/#/route`) for accessibility / middle-click / open-in-new-tab.
     */
    return (
        <SideNav aria-label="Navigation" data-testid="nav-sidebar">
            {sections.map(section => (
                <SideNav.Group
                    label={section.title}
                    key={section.id}
                    data-testid={`nav-section-${section.id}`}
                >
                    {section.items.map(item => (
                        <SidebarItem key={item.path} item={item} />
                    ))}
                </SideNav.Group>
            ))}
        </SideNav>
    );
});
